<template>
  <main class="user-journeys">
    <header class="page-head">
      <h1>User journeys <small class="admin-note">(Admin/Mod)</small></h1>
      <p class="sub">
        Explore individual visitors grouped by account or browser, with counts of events and sessions.
      </p>
    </header>

    <!-- Identities list -->
    <section class="card">
   <header class="card__head">
  <h2>Visitor identities</h2>
  <div class="controls">
    <label>
      Range:
      <select v-model.number="days" @change="fetchIdentities">
        <option :value="7">7 days</option>
        <option :value="30">30 days</option>
        <option :value="90">90 days</option>
        <option :value="180">180 days</option>
      </select>
    </label>
    <label>
      Max rows:
      <select v-model.number="limit" @change="fetchIdentities">
        <option :value="50">50</option>
        <option :value="100">100</option>
        <option :value="200">200</option>
      </select>
    </label>
    <input
      v-model.trim="search"
      class="input"
      placeholder="Filter by label, username, UA, IP snippet…"
      style="min-width:260px"
    />
    <label>
      <input type="checkbox" v-model="showIPs" />
      Show IPs
    </label>
  </div>
</header>


      <div v-if="loading" class="skeleton">
        <div class="skeleton-row" v-for="n in 6" :key="'s'+n"></div>
      </div>

      <div v-else>
        <p class="muted" v-if="range.start">
          Window: {{ range.start }} → {{ range.end }} ({{ range.days }} days).
          Showing {{ filteredIdentities.length.toLocaleString() }} of
          {{ identities.length.toLocaleString() }} identities.
        </p>

      <table class="mini">
  <thead>
    <tr>
      <th>Label</th>
      <th>User</th>
      <th class="num">Sessions</th>
      <th class="num">Events</th>
      <th>First seen</th>
      <th>Last seen</th>
      <th>User agent (sample)</th>
      <th v-if="showIPs" class="mono">IP</th> <!-- NEW -->
    </tr>
  </thead>
  <tbody>
    <tr
      v-for="id in filteredIdentities"
      :key="id.identity_key"
      @click="selectIdentity(id)"
      :class="{ 'is-selected': selectedIdentity && selectedIdentity.identity_key === id.identity_key }"
      style="cursor:pointer"
    >
      <td>{{ id.label }}</td>
      <td>
        <span v-if="id.username">{{ id.username }}</span>
        <span v-else-if="id.user_id">#{{ id.user_id }}</span>
        <span v-else class="muted">Anonymous</span>
      </td>
      <td class="num">{{ id.sessions.toLocaleString() }}</td>
      <td class="num">{{ id.events.toLocaleString() }}</td>
      <td>{{ id.first_seen }}</td>
      <td>{{ id.last_seen }}</td>
      <td>
        <span class="muted">{{ id.user_agent_sample || '–' }}</span>
      </td>
      <td v-if="showIPs" class="mono">
        <span v-if="id.ip_sample">{{ id.ip_sample }}</span>
        <span v-else class="muted">–</span>
      </td>
    </tr>
    <tr v-if="!filteredIdentities.length">
      <td :colspan="identitiesColSpan" class="empty">
        No identities found for this window.
      </td>
    </tr>
  </tbody>
</table>

      </div>
    </section>

    <!-- Selected identity summary -->
    <section v-if="selectedIdentity" class="card">
  <header class="card__head">
    <h2>Selected identity</h2>
  </header>
  <div class="grid kpis">
    <article class="kpi">
      <h3>Label</h3>
      <div class="kpi__value">{{ selectedIdentity.label }}</div>
    </article>
    <article class="kpi">
      <h3>User</h3>
      <div class="kpi__value">
        <span v-if="selectedIdentity.username">{{ selectedIdentity.username }}</span>
        <span v-else-if="selectedIdentity.user_id">#{{ selectedIdentity.user_id }}</span>
        <span v-else>Anonymous</span>
      </div>
    </article>
    <article class="kpi">
      <h3>Sessions</h3>
      <div class="kpi__value">{{ selectedIdentity.sessions.toLocaleString() }}</div>
    </article>
    <article class="kpi">
      <h3>Events</h3>
      <div class="kpi__value">{{ selectedIdentity.events.toLocaleString() }}</div>
    </article>

    <!-- NEW: IP overview -->
    <article class="kpi" v-if="showIPs">
      <h3>IP</h3>
      <div class="kpi__value mono">
        <span v-if="selectedIdentity.ip_sample">{{ selectedIdentity.ip_sample }}</span>
        <span v-else class="muted">–</span>
      </div>
      <p class="kpi__hint" v-if="selectedIdentity.ip_count > 1">
        {{ selectedIdentity.ip_count }} distinct IPs
      </p>
      <p class="kpi__hint" v-else>
        Single IP seen
      </p>
    </article>
  </div>
  <p class="muted" style="margin-top:.5rem">
    Below is a chronological list of sessions and events for this identity within the selected date range.
  </p>
</section>

    <!-- Timeline, grouped by session -->
    <section v-if="selectedIdentity" class="card">
      <header class="card__head">
        <h2>Journey timeline</h2>
        <div class="controls">
          <label>
            Rows:
            <select v-model.number="timelineLimit" @change="refetchTimeline">
              <option :value="200">200</option>
              <option :value="500">500</option>
              <option :value="1000">1000</option>
            </select>
          </label>
        </div>
      </header>

      <div v-if="timelineLoading" class="skeleton">
        <div class="skeleton-row" v-for="n in 6" :key="'t'+n"></div>
      </div>

      <div v-else>
        <p v-if="timelineError" class="error">
          {{ timelineError }}
        </p>

        <p class="muted" v-if="!timelineError && timeline.length">
          Showing {{ timeline.length.toLocaleString() }} events (max {{ timelineLimit.toLocaleString() }})
          for identity {{ selectedIdentity.identity_key }} across
          {{ sessionGroups.length.toLocaleString() }} session(s).
        </p>

        <p v-if="!timeline.length && !timelineError" class="muted">
          No events found for this identity in the selected window.
        </p>

        <div v-for="(session, sIdx) in sessionGroups" :key="session.session_id || 'nosession-' + sIdx" class="session-block">
          <header class="session-block__head">
            <div>
              <strong>Session {{ session.shortSessionId }}</strong>
              <span class="muted" v-if="session.session_id === null"> (no session id)</span>
              <div class="muted">
                {{ session.first_at }} → {{ session.last_at }}
              </div>
            </div>
            <div class="session-block__meta">
              <span>{{ session.events.length.toLocaleString() }} events</span>
              <span>•</span>
              <span v-if="session.is_authed === 1">Authed</span>
              <span v-else-if="session.is_authed === 0" class="muted">Anon</span>
              <span v-else class="muted">Auth mixed/unknown</span>
            </div>
          </header>

          <div v-if="session.routeTrail && session.routeTrail.length" class="session-block__routes muted">
            <span v-for="(r, idx) in session.routeTrail" :key="idx">
              <span v-if="idx > 0"> → </span>{{ r || '(none)' }}
            </span>
          </div>

          <table class="mini">
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Route</th>
                <th>Path</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(ev, idx) in session.events" :key="idx">
                <td>{{ ev.occurred_at }}</td>
                <td>
                  <span v-if="ev.event_type === 'pageview'">Pageview</span>
                  <span v-else>Event</span>
                </td>
                <td>{{ ev.route_name || '(none)' }}</td>
                <td class="mono">{{ ev.path || '–' }}</td>
                <td>
                  <template v-if="ev.event_type === 'user_event'">
                    <strong>{{ ev.event_name || '(event)' }}</strong>
                    <span v-if="ev.element_text"> — {{ shorten(ev.element_text, 80) }}</span>
                  </template>
                  <template v-else>
                    <span v-if="ev.from_route">
                      From: {{ ev.from_route }}
                    </span>
                    <span v-else class="muted">
                      (no extra details)
                    </span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </section>
  </main>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminUserJourneys',
  data() {
    return {
      days: 30,
      limit: 100,
      loading: false,
      identities: [],
      range: { start: '', end: '', days: 0 },
      search: '',
      selectedIdentity: null,

    // IP visibility toggle
    showIPs: false,
      // timeline state
      timeline: [],
      timelineLoading: false,
      timelineError: '',
      timelineLimit: 500,
    };
  },
  computed: {
   filteredIdentities() {
  const q = this.search.trim().toLowerCase();
  if (!q) return this.identities;

  return this.identities.filter((id) => {
    const label = (id.label || '').toLowerCase();
    const username = (id.username || '').toLowerCase();
    const ua = (id.user_agent_sample || '').toLowerCase();
    const key = (id.identity_key || '').toLowerCase();
    const ip  = (id.ip_sample || '').toLowerCase(); // NEW

    return (
      label.includes(q) ||
      username.includes(q) ||
      ua.includes(q) ||
      key.includes(q) ||
      ip.includes(q)       // NEW
    );
  });
},
  identitiesColSpan() {
    // 7 columns + 1 optional IP column
    return this.showIPs ? 8 : 7;
  },

    // Group the flat timeline into per-session blocks
    sessionGroups() {
      if (!this.timeline || !this.timeline.length) return [];

      const groupsMap = new Map();

      this.timeline.forEach((ev) => {
        const sid = ev.session_id || null;
        const key = sid || 'nosession';

        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            session_id: sid,
            shortSessionId: sid ? this.shorten(sid, 12) : '(none)',
            events: [],
            first_at: ev.occurred_at,
            last_at: ev.occurred_at,
            is_authed: ev.is_authed, // first guess, may be overwritten
            routeTrail: [],
          });
        }

        const group = groupsMap.get(key);
        group.events.push(ev);

        // update first/last
        if (ev.occurred_at < group.first_at) group.first_at = ev.occurred_at;
        if (ev.occurred_at > group.last_at) group.last_at = ev.occurred_at;

        // auth status: if any event is authed, mark as 1; otherwise keep 0 or null
        if (ev.is_authed === 1) {
          group.is_authed = 1;
        } else if (group.is_authed === undefined || group.is_authed === null) {
          group.is_authed = ev.is_authed;
        }

        // route trail: only push when route_name changes
        if (ev.route_name) {
          const trail = group.routeTrail;
          const lastRoute = trail.length ? trail[trail.length - 1] : null;
          if (ev.route_name !== lastRoute) {
            trail.push(ev.route_name);
          }
        }
      });

      // Convert to array and sort by first_at
      const arr = Array.from(groupsMap.values());
      arr.forEach((g) => {
        g.events.sort((a, b) => {
          if (a.occurred_at === b.occurred_at) {
            // tie-breaker by client_ts if present
            const at = a.client_ts || 0;
            const bt = b.client_ts || 0;
            return at - bt;
          }
          return a.occurred_at < b.occurred_at ? -1 : 1;
        });
      });

      arr.sort((a, b) => (a.first_at < b.first_at ? -1 : 1));
      return arr;
    },
  },
  methods: {
    authHeader() {
      const token = localStorage.getItem('token') || '';
      return { Authorization: `Bearer ${token}` };
    },

    async fetchIdentities() {
      this.loading = true;
      this.selectedIdentity = null;
      this.timeline = [];
      this.timelineError = '';

      try {
        const res = await axios.get('/etherscan/fetch_journey_identities.php', {
          headers: this.authHeader(),
          params: {
            days: this.days,
            limit: this.limit,
          },
        });

        if (res.data?.status !== 'success') {
          console.error('Failed to fetch identities', res.data);
          this.identities = [];
          return;
        }

        this.range = res.data.range || this.range;
    this.identities = (res.data.identities || []).map((row) => {
  return {
    identity_key: row.identity_key,
    user_id: row.user_id,
    username: row.username,
    label: row.label,
    sessions: row.sessions,
    events: row.events,
    first_seen: row.first_seen,
    last_seen: row.last_seen,
    user_agent_sample: row.user_agent_sample,
    ip_sample: row.ip_sample,      // NEW
    ip_count: row.ip_count || 0,   // NEW
  };
});

      } catch (e) {
        console.error('Error fetching identities', e);
        this.identities = [];
      } finally {
        this.loading = false;
      }
    },

    selectIdentity(id) {
      this.selectedIdentity = id;
      this.timeline = [];
      this.timelineError = '';
      this.fetchTimeline(id.identity_key);
    },

    async fetchTimeline(identityKey) {
      if (!identityKey) return;
      this.timelineLoading = true;
      this.timelineError = '';
      this.timeline = [];

      try {
        const res = await axios.get('/etherscan/fetch_journey_timeline.php', {
          headers: this.authHeader(),
          params: {
            identity_key: identityKey,
            days: this.days,
            limit: this.timelineLimit,
          },
        });

        if (res.data?.status !== 'success') {
          console.error('Failed to fetch timeline', res.data);
          this.timelineError = res.data.message || 'Failed to fetch timeline.';
          return;
        }

        this.timeline = res.data.timeline || [];
      } catch (e) {
        console.error('Error fetching timeline', e);
        this.timelineError = 'Network or server error while fetching timeline.';
      } finally {
        this.timelineLoading = false;
      }
    },

    refetchTimeline() {
      if (this.selectedIdentity) {
        this.fetchTimeline(this.selectedIdentity.identity_key);
      }
    },

    shorten(text, max = 80) {
      if (!text) return '';
      if (text.length <= max) return text;
      return text.slice(0, max - 1) + '…';
    },
  },
  mounted() {
    this.fetchIdentities();
  },
};
</script>
